// BLE FTMS / ANT+-over-BLE / PM5 discovery + streaming controller.
//
// Web Bluetooth only exposes GATT (BLE). Native ANT+ FE-C radios are bridged by
// the equipment into BLE FTMS or a proprietary GATT service, so discovery is
// strictly scoped to:
//   * FTMS service 0x1826 (treadmills, bikes, ellipticals, rowers, climbers)
//   * Concept2 PM5 proprietary service (rowers / ski ergs broadcasting power)
// plus an optional Heart Rate service for live HR fusion.

import {
  FTMS_DATA_CHARACTERISTICS,
  FTMS_SERVICE_UUID,
  HEART_RATE_MEASUREMENT_UUID,
  HEART_RATE_SERVICE_UUID,
  MACHINE_CAPABILITIES,
  PM5_CHARACTERISTICS,
  PM5_SERVICE_UUID,
} from './hardwareCatalog'
import { parseFtmsByKind, parseHeartRateMeasurement } from './ftmsParser'
import { parsePm5Characteristic } from './pm5Parser'
import { ConnectionStateMachine, type GapDescriptor, type RecoveryState } from './connectionState'
import type {
  DiscoveredPeripheral,
  MachineKind,
  PeripheralConnectionState,
  TelemetryFrame,
} from './types'

export function isWebBluetoothAvailable(): boolean {
  return typeof navigator !== 'undefined' && 'bluetooth' in navigator
}

type FrameListener = (frame: TelemetryFrame) => void
type StateListener = (state: PeripheralConnectionState, detail?: string) => void
type RecoveryListener = (state: RecoveryState, gap?: GapDescriptor) => void

export class BleTelemetryController {
  private device: BluetoothDevice | null = null
  private server: BluetoothRemoteGATTServer | null = null
  private machineKind: MachineKind = 'unknown'
  private state: PeripheralConnectionState = 'idle'
  private frameListeners = new Set<FrameListener>()
  private stateListeners = new Set<StateListener>()
  private recoveryListeners = new Set<RecoveryListener>()
  private activeCharacteristics: BluetoothRemoteGATTCharacteristic[] = []
  private stateMachine: ConnectionStateMachine | null = null
  private isPm5 = false
  /** Distinguishes a user-initiated teardown from a noisy-RF drop. */
  private intentionalDisconnect = false

  onFrame(listener: FrameListener): () => void {
    this.frameListeners.add(listener)
    return () => this.frameListeners.delete(listener)
  }

  onStateChange(listener: StateListener): () => void {
    this.stateListeners.add(listener)
    return () => this.stateListeners.delete(listener)
  }

  /** Recovery state machine transitions (CONNECTED / RECONNECTING_SILENT / FALLBACK). */
  onRecoveryStateChange(listener: RecoveryListener): () => void {
    this.recoveryListeners.add(listener)
    return () => this.recoveryListeners.delete(listener)
  }

  getState(): PeripheralConnectionState {
    return this.state
  }

  getRecoveryState(): RecoveryState {
    return this.stateMachine?.getState() ?? 'CONNECTED'
  }

  /** Manual retry from the DISCONNECTED_FALLBACK state. */
  resumeFromFallback(): void {
    this.stateMachine?.resume()
  }

  private setState(state: PeripheralConnectionState, detail?: string) {
    this.state = state
    for (const l of this.stateListeners) l(state, detail)
  }

  private emit(metrics: TelemetryFrame['metrics']) {
    if (Object.keys(metrics).length === 0) return
    const frame: TelemetryFrame = { timestamp: Date.now(), machineKind: this.machineKind, metrics }
    for (const l of this.frameListeners) l(frame)
  }

  /**
   * Prompts the OS chooser, scoped to fitness-machine peripherals only.
   * Returns the discovered peripheral descriptor (not yet streaming).
   */
  async discover(): Promise<DiscoveredPeripheral> {
    if (!isWebBluetoothAvailable()) {
      throw new Error('Web Bluetooth is not available in this browser. Use Chrome/Edge over HTTPS.')
    }

    this.setState('scanning')
    let device: BluetoothDevice
    try {
      device = await navigator.bluetooth.requestDevice({
        filters: [
          { services: [FTMS_SERVICE_UUID] },
          { services: [PM5_SERVICE_UUID] },
        ],
        optionalServices: [HEART_RATE_SERVICE_UUID, FTMS_SERVICE_UUID, PM5_SERVICE_UUID],
      })
    } catch (err) {
      this.setState('error', err instanceof Error ? err.message : 'Discovery cancelled')
      throw err
    }

    this.device = device

    // Build the recovery state machine; its reconnect callback re-establishes
    // GATT and re-subscribes the same characteristics.
    this.stateMachine = new ConnectionStateMachine(() => this.reconnectInternal())
    this.stateMachine.onStateChange((recoveryState, detail) => {
      for (const l of this.recoveryListeners) l(recoveryState, detail?.gap)
      // Hold the live data view during silent reconnects; only surrender the
      // peripheral state when the grace window is exhausted.
      if (recoveryState === 'RECONNECTING_SILENT') this.setState('streaming', 'reconnecting')
      else if (recoveryState === 'CONNECTED') this.setState('streaming')
      else if (recoveryState === 'DISCONNECTED_FALLBACK') this.setState('disconnected', 'fallback')
    })

    device.addEventListener('gattserverdisconnected', () => {
      this.activeCharacteristics = []
      if (this.intentionalDisconnect) {
        this.setState('disconnected')
        return
      }
      // Noisy-RF / step-off drop — let the state machine attempt silent recovery.
      this.stateMachine?.handleDrop()
    })

    return {
      id: device.id,
      name: device.name ?? 'Fitness Machine',
      connectionType: 'LOCAL_BLE_FTMS',
      machineKind: 'unknown',
      capabilities: [],
    }
  }

  /** Connects GATT, resolves the machine kind, and subscribes to telemetry. */
  async connectAndStream(): Promise<DiscoveredPeripheral> {
    if (!this.device?.gatt) throw new Error('No device discovered. Call discover() first.')

    this.setState('connecting')
    await this.attachStreams()

    this.setState('streaming')
    return {
      id: this.device.id,
      name: this.device.name ?? 'Fitness Machine',
      connectionType: this.isPm5 ? 'LOCAL_ANT_PLUS' : 'LOCAL_BLE_FTMS',
      machineKind: this.machineKind,
      capabilities: MACHINE_CAPABILITIES[this.machineKind] ?? [],
    }
  }

  /** (Re)connect GATT and subscribe to the relevant data + HR characteristics. */
  private async attachStreams(): Promise<void> {
    if (!this.device?.gatt) throw new Error('No device to connect')
    this.server = await this.device.gatt.connect()

    const services = await this.server.getPrimaryServices()
    const serviceUuids = new Set(services.map((s) => s.uuid))
    this.isPm5 = serviceUuids.has(normalize128(PM5_SERVICE_UUID))

    this.activeCharacteristics = []
    if (this.isPm5) {
      this.machineKind = 'rower'
      await this.subscribePm5()
    } else {
      await this.subscribeFtms()
    }
    await this.subscribeHeartRate(services).catch(() => undefined)
  }

  /** Single reconnect attempt driven by the state machine; true on success. */
  private async reconnectInternal(): Promise<boolean> {
    if (!this.device?.gatt || this.intentionalDisconnect) return false
    try {
      await this.attachStreams()
      return this.server?.connected ?? false
    } catch {
      return false
    }
  }

  private async subscribeFtms() {
    if (!this.server) return
    const service = await this.server.getPrimaryService(FTMS_SERVICE_UUID)
    const characteristics = await service.getCharacteristics()

    for (const ch of characteristics) {
      const match = Object.values(FTMS_DATA_CHARACTERISTICS).find(
        (entry) => normalize16(entry.uuid) === ch.uuid,
      )
      if (!match || !ch.properties.notify) continue

      this.machineKind = match.kind
      await ch.startNotifications()
      ch.addEventListener('characteristicvaluechanged', (event) => {
        const value = (event.target as BluetoothRemoteGATTCharacteristic).value
        if (value) this.emit(parseFtmsByKind(this.machineKind, value))
      })
      this.activeCharacteristics.push(ch)
    }
  }

  private async subscribePm5() {
    if (!this.server) return
    const service = await this.server.getPrimaryService(PM5_SERVICE_UUID)
    const wanted = [
      PM5_CHARACTERISTICS.additionalStatus1,
      PM5_CHARACTERISTICS.additionalStatus2,
      PM5_CHARACTERISTICS.generalStatus,
      PM5_CHARACTERISTICS.strokeData,
    ]

    for (const uuid of wanted) {
      const ch = await service.getCharacteristic(uuid).catch(() => null)
      if (!ch || !ch.properties.notify) continue
      await ch.startNotifications()
      ch.addEventListener('characteristicvaluechanged', (event) => {
        const target = event.target as BluetoothRemoteGATTCharacteristic
        if (target.value) this.emit(parsePm5Characteristic(target.uuid, target.value))
      })
      this.activeCharacteristics.push(ch)
    }
  }

  private async subscribeHeartRate(services: BluetoothRemoteGATTService[]) {
    const hr = services.find((s) => s.uuid === normalize16(HEART_RATE_SERVICE_UUID))
    if (!hr) return
    const ch = await hr.getCharacteristic(HEART_RATE_MEASUREMENT_UUID).catch(() => null)
    if (!ch || !ch.properties.notify) return
    await ch.startNotifications()
    ch.addEventListener('characteristicvaluechanged', (event) => {
      const value = (event.target as BluetoothRemoteGATTCharacteristic).value
      if (value) this.emit(parseHeartRateMeasurement(value))
    })
    this.activeCharacteristics.push(ch)
  }

  async disconnect() {
    // Mark first so the gattserverdisconnected handler treats this as a clean
    // teardown and does not arm the recovery state machine.
    this.intentionalDisconnect = true
    this.stateMachine?.dispose()
    this.stateMachine = null
    for (const ch of this.activeCharacteristics) {
      await ch.stopNotifications().catch(() => undefined)
    }
    this.activeCharacteristics = []
    if (this.server?.connected) this.server.disconnect()
    this.setState('disconnected')
  }
}

/** Expand a 16-bit UUID to the canonical 128-bit string Web Bluetooth reports. */
function normalize16(uuid: number): string {
  return `0000${uuid.toString(16).padStart(4, '0')}-0000-1000-8000-00805f9b34fb`
}

function normalize128(uuid: string): string {
  return uuid.toLowerCase()
}
